import React, { useState, useEffect, useRef } from 'react';
import './ManualRiderModal.css';

// Custom Searchable Dropdown for Riders
const CustomRiderSelect = ({ value, onChange, options, placeholder }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState(value);
    const [dropdownStyle, setDropdownStyle] = useState({});
    const wrapperRef = useRef(null);
    const listRef = useRef(null);

    useEffect(() => {
        setSearch(value);
    }, [value]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                // Also check if clicking inside the fixed list
                if (listRef.current && listRef.current.contains(event.target)) {
                    return;
                }
                setIsOpen(false);
                setSearch(value); // Revert to current value on close
            }
        };

        const updatePosition = () => {
            if (wrapperRef.current) {
                const rect = wrapperRef.current.getBoundingClientRect();
                setDropdownStyle({
                    position: 'fixed',
                    top: `${rect.bottom}px`,
                    left: `${rect.left}px`,
                    width: `${rect.width}px`,
                    zIndex: 9999,
                    maxHeight: '240px',
                    overflowY: 'auto'
                });
            }
        };

        const handleScroll = (event) => {
            // Close only if scrolling inside the dropdown list itself
            if (isOpen && listRef.current && listRef.current.contains(event.target)) {
                return;
            }
            // Otherwise update position
            updatePosition();
        };

        if (isOpen) {
            updatePosition();

            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('scroll', handleScroll, true); // Capture for all scrolls
            window.addEventListener('resize', () => setIsOpen(false));
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('scroll', handleScroll, true);
            window.removeEventListener('resize', () => setIsOpen(false));
        };
    }, [isOpen, value]);

    const filteredOptions = options.filter(opt => {
        const lowerSearch = search.toLowerCase();
        return opt.name.toLowerCase().includes(lowerSearch) ||
            (opt.teamname && opt.teamname.toLowerCase().includes(lowerSearch)) ||
            (opt.team_abbr && opt.team_abbr.toLowerCase().includes(lowerSearch));
    });

    const handleSelect = (name) => {
        onChange(name);
        setSearch(name);
        setIsOpen(false);
    };

    return (
        <div className={`custom-select-wrapper ${isOpen ? 'is-open' : ''}`} ref={wrapperRef}>
            <div className="custom-select-input-container">
                <input
                    type="text"
                    className="custom-select-input"
                    placeholder={placeholder}
                    value={search}
                    onChange={(e) => {
                        setSearch(e.target.value);
                        onChange(e.target.value);
                        setIsOpen(true);
                    }}
                    onFocus={() => setIsOpen(true)}
                    onClick={() => {
                        setIsOpen(true);
                        setSearch(''); // Clear text to show all options
                    }}
                    maxLength={100}
                />
                {search && (
                    <button
                        className="custom-select-clear-button"
                        onClick={(e) => {
                            e.stopPropagation();
                            setSearch('');
                            onChange('');
                        }}
                        title="Invoer wissen"
                    >
                        ✕
                    </button>
                )}
            </div>
            {isOpen && filteredOptions.length > 0 && (
                <div
                    className="custom-select-list"
                    style={dropdownStyle}
                    ref={listRef}
                >
                    {filteredOptions.map(opt => (
                        <div
                            key={opt.name}
                            className="custom-select-item"
                            onClick={() => handleSelect(opt.name)}
                        >
                            <span className="rider-name">{opt.name}</span>
                            <span className="rider-price">€ {(opt.price / 1000000).toFixed(2)}M</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// Custom Dropdown for Races
const CustomRaceSelect = ({ value, onChange, options, placeholder }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [dropdownStyle, setDropdownStyle] = useState({});
    const wrapperRef = useRef(null);
    const listRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                if (listRef.current && listRef.current.contains(event.target)) {
                    return;
                }
                setIsOpen(false);
            }
        };

        const updatePosition = () => {
            if (wrapperRef.current) {
                const rect = wrapperRef.current.getBoundingClientRect();
                setDropdownStyle({
                    position: 'fixed',
                    top: `${rect.bottom}px`,
                    left: `${rect.left}px`,
                    width: `${rect.width}px`,
                    zIndex: 9999,
                    maxHeight: '240px',
                    overflowY: 'auto'
                });
            }
        };

        const handleScroll = (event) => {
            // Close only if scrolling inside the dropdown list itself
            if (isOpen && listRef.current && listRef.current.contains(event.target)) {
                return;
            }
            // Otherwise update position
            updatePosition();
        };

        if (isOpen) {
            updatePosition();

            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('scroll', handleScroll, true);
            window.addEventListener('resize', () => setIsOpen(false));
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('scroll', handleScroll, true);
            window.removeEventListener('resize', () => setIsOpen(false));
        };
    }, [isOpen]);

    return (
        <div className={`custom-select-wrapper ${isOpen ? 'is-open' : ''}`} ref={wrapperRef}>
            <div
                className={`custom-select-trigger ${isOpen ? 'open' : ''} ${!value ? 'placeholder' : ''}`}
                onClick={() => setIsOpen(!isOpen)}
                tabIndex={0}
            >
                {value || placeholder}
                <span className="arrow">▼</span>
            </div>
            {isOpen && (
                <div
                    className="custom-select-list"
                    style={dropdownStyle}
                    ref={listRef}
                >
                    {options.map(opt => (
                        <div
                            key={opt}
                            className={`custom-select-item ${value === opt ? 'selected' : ''}`}
                            onClick={() => {
                                onChange(opt);
                                setIsOpen(false);
                            }}
                        >
                            {opt}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

function ManualRiderModal({ isOpen, onClose, onSave, allRiders, allRaces, currentCustomRiders }) {
    const [rows, setRows] = useState([]);
    const [errors, setErrors] = useState([]);

    // Initialize rows with existing custom riders or one empty row
    useEffect(() => {
        if (isOpen) {
            if (currentCustomRiders && currentCustomRiders.length > 0) {
                // Map existing to rows, plus one empty row at the end
                const existingRows = currentCustomRiders.map((cr, index) => ({
                    id: Date.now() + index,
                    rider: cr.rider,
                    race: cr.race
                }));
                setRows([...existingRows, { id: Date.now() + existingRows.length, rider: '', race: '' }]);
            } else {
                setRows([{ id: Date.now(), rider: '', race: '' }]);
            }
            setErrors([]);
        }
    }, [isOpen, currentCustomRiders]);

    const handleRiderChange = (id, value) => {
        const newRows = rows.map(row => {
            if (row.id === id) {
                // When rider changes, we might need to clear the race if it's invalid for new rider
                // But for now, let's keep it and let validation/dropdown handle it
                return { ...row, rider: value };
            }
            return row;
        });

        // Check if we need to add a new empty row
        const updatedRow = newRows.find(r => r.id === id);
        if (updatedRow.rider && updatedRow.race && newRows[newRows.length - 1].id === id) {
            newRows.push({ id: Date.now(), rider: '', race: '' });
        }

        setRows(newRows);
        // Clear errors when user types
        if (errors.length > 0) setErrors([]);
    };

    const handleRaceChange = (id, value) => {
        const newRows = rows.map(row =>
            row.id === id ? { ...row, race: value } : row
        );

        // Check if we need to add a new empty row
        const updatedRow = newRows.find(r => r.id === id);
        if (updatedRow.rider && updatedRow.race && newRows[newRows.length - 1].id === id) {
            newRows.push({ id: Date.now(), rider: '', race: '' });
        }

        setRows(newRows);
    };

    const handleRemoveRow = (id) => {
        const newRows = rows.filter(row => row.id !== id);
        // Ensure there is always at least one row (empty if needed)
        if (newRows.length === 0) {
            newRows.push({ id: Date.now(), rider: '', race: '' });
        }
        setRows(newRows);
    };

    const handleSave = () => {
        const validRows = rows.filter(r => r.rider && r.race);

        // Validate: Riders must exist in the official price list
        const invalidRiders = validRows.filter(r => !allRiders[r.rider]);
        if (invalidRiders.length > 0) {
            setErrors(invalidRiders.map(r => `Renner '${r.rider}' staat niet op de officiële prijslijst en kan niet worden toegevoegd.`));
            return;
        }

        // Validate: Duplicates
        for (const row of validRows) {
            // Check against API/DB Data
            // RELAXED VALIDATION: Allow adding riders even if they are already in the DB.
            // This allows the manual entry to act as a "backup" if they are removed from the official list later.
            /*
            const existingRaces = allRiders[row.rider]?.races || [];
            if (existingRaces.includes(row.race)) {
                setErrors([`Renner '${row.rider}' doet al mee aan '${row.race}'.`]);
                return;
            }
            */

            // Check for duplicates within the form itself
            const duplicates = validRows.filter(r => r.rider === row.rider && r.race === row.race);
            if (duplicates.length > 1) {
                setErrors([`Je hebt '${row.rider}' voor '${row.race}' dubbel ingevuld.`]);
                return;
            }
        }

        onSave(validRows.map(r => ({ rider: r.rider, race: r.race })));
        onClose();
    };

    // Helper to filter races for a specific row
    const getAvailableRaces = (riderName, rowId) => {
        if (!riderName) return allRaces; // If no rider selected, show all (or maybe none?) - show all is fine

        // 1. Races already in DB
        const dbRaces = allRiders[riderName]?.races || [];

        // 2. Races selected in OTHER rows for this rider
        const otherRowsRaces = rows
            .filter(r => r.id !== rowId && r.rider === riderName && r.race)
            .map(r => r.race);

        const excluded = [...dbRaces, ...otherRowsRaces];
        return allRaces.filter(race => !excluded.includes(race));
    };

    if (!isOpen) return null;

    // Extract rider names for datalist, sorted by price (desc) then name
    // allRiders is object { riderName: { price, races } }
    const riderOptions = Object.keys(allRiders || {}).map(name => ({
        name,
        price: allRiders[name]?.price || 0,
        teamname: allRiders[name]?.teamname || '',
        team_abbr: allRiders[name]?.team_abbr || ''
    })).sort((a, b) => {
        if (b.price !== a.price) {
            return b.price - a.price; // Descending price
        }
        return a.name.localeCompare(b.name); // Ascending name
    });

    return (
        <div className="manual-modal-overlay" onClick={onClose}>
            <div className="manual-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="manual-modal-header">
                    <h3>Handmatig toegevoegde renners</h3>
                    <button className="close-btn" onClick={(e) => { 
                        e.preventDefault(); 
                        e.stopPropagation(); 
                        setTimeout(onClose, 50); 
                    }}>×</button>
                </div>

                <div className="manual-modal-body">
                    <p className="description">
                        Voeg hier renners toe die je mist op de startlijst.
                    </p>

                    {errors.length > 0 && (
                        <div className="error-messages">
                            {errors.map((err, idx) => <div key={idx} className="error-msg">{err}</div>)}
                        </div>
                    )}

                    <div className="rows-container">
                        {rows.map((row) => (
                            <div key={row.id} className="input-row">
                                <div className="input-group rider-group">
                                    <CustomRiderSelect
                                        value={row.rider}
                                        onChange={(val) => handleRiderChange(row.id, val)}
                                        options={riderOptions}
                                        placeholder="Zoek renner of ploeg..."
                                    />
                                </div>

                                <div className="input-group race-group">
                                    <CustomRaceSelect
                                        value={row.race}
                                        onChange={(val) => handleRaceChange(row.id, val)}
                                        options={getAvailableRaces(row.rider, row.id)}
                                        placeholder="Kies wedstrijd..."
                                    />
                                </div>

                                <button
                                    className="remove-row-btn"
                                    onClick={() => handleRemoveRow(row.id)}
                                    title="Verwijder regel"
                                    style={{ visibility: (row.rider || row.race) ? 'visible' : 'hidden' }}
                                >
                                    ✖
                                </button>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="manual-modal-footer">
                    <button className="cancel-btn" onClick={onClose}>Annuleren</button>
                    <button className="save-btn" onClick={handleSave}>Opslaan</button>
                </div>
            </div>
        </div>
    );
}

export default ManualRiderModal;
